/**
 * On-screen touch controls: a floating stick on the left, buttons on the right.
 *
 * The layout follows the shape mobile platformers have settled on: the left
 * half of the screen is a thumb zone where a stick appears under the finger
 * (so the thumb never has to find a fixed widget), and the right side holds the
 * two buttons a course needs — jump and run/fire. Everything is drawn in HTML
 * over the canvases, styled in `index.html`, and feeds the same `Input` actions
 * the keyboard does, so gameplay code does not know which one is in use.
 *
 * Only the *decisions* live here; the shape maths is in exported pure helpers
 * so it can be tested without a DOM.
 *
 * Touch and scroll handling is deliberate rather than incidental: the layers
 * set `touch-action: none`, swallow `contextmenu`, track each pointer by id and
 * release it on `pointercancel`, so a lost finger or a browser gesture cannot
 * leave the player running into a pit. `Input` drops all held actions on
 * `blur`/`visibilitychange` for the same reason.
 */

import type { Input, InputAction } from './input'

/** Stick travel in CSS pixels; the knob stops at this distance. */
export const STICK_RADIUS = 62
/**
 * Direction thresholds, in CSS pixels from the stick's origin.
 *
 * Engage is how far the thumb moves to start a direction. Flip is how far past
 * the centre it must come back to *reverse* one, and release is how close to
 * the centre it must settle to let go. The asymmetry is the whole point: a
 * standing start needs a deliberate nudge, but once you are running, snapping
 * the other way has to be a few pixels — the game is built around that move —
 * and the old radial dead zone (35% of the travel, both ways) made it a sweep.
 *
 * Vertical stays wider than horizontal because `down` is a crouch or a
 * drop-through, which is destructive when it fires while steering.
 */
export const STICK_DEADZONE_X = 7
export const STICK_DEADZONE_Y = 14
/** Travel past the centre that reverses an engaged direction. */
export const STICK_FLIP_X = 3
export const STICK_FLIP_Y = 5
/** Distance back to the centre that releases an engaged direction. */
export const STICK_RELEASE_X = 4
export const STICK_RELEASE_Y = 8

export interface StickVector {
  x: number
  y: number
}

export type StickActions = {
  left: boolean
  right: boolean
  up: boolean
  down: boolean
}

/** One finger on the action pad: where it is now, and what it has been over. */
export interface PadPointer {
  over: 'jump' | 'run' | null
  /** Every pad button this finger has touched since it went down. Read-only to
   * `padHeld`, which never mutates it. */
  touched: Set<'jump' | 'run'>
}

/**
 * What the pad's pointers add to the run and jump actions.
 *
 * The two buttons are deliberately *not* symmetric:
 *
 *   - **Run is sticky for the touch.** Once a finger has visited it, run stays
 *     held until that finger lifts, even while the thumb travels onto jump.
 *     That is what makes "long-press run, drag to jump" a single gesture.
 *   - **Jump follows presence.** It is held while a finger is on it and released
 *     the moment that finger leaves, so swiping off and back on presses it
 *     again — as many times as you like with one finger down. A sticky jump
 *     could only ever fire once per touch.
 *
 * Neither is a toggle: there is no state that outlives a finger.
 */
export function padHeld(pointers: readonly PadPointer[]): {
  jump: boolean
  run: boolean
  holdingRun: boolean
} {
  const holdingRun = pointers.some((p) => p.touched.has('run'))
  return {
    jump: pointers.some((p) => p.over === 'jump'),
    run: holdingRun,
    holdingRun,
  }
}

/** Clamp a thumb offset to the stick's travel. */
export function stickVector(
  dx: number,
  dy: number,
  radius = STICK_RADIUS,
): StickVector {
  const len = Math.hypot(dx, dy)
  if (len <= radius || len === 0) return { x: dx, y: dy }
  const k = radius / len
  return { x: dx * k, y: dy * k }
}

/** The stick's resting state. */
export const NEUTRAL_STICK: StickActions = {
  left: false,
  right: false,
  up: false,
  down: false,
}

/**
 * One axis of the stick, with the engage/flip/release hysteresis.
 *
 * Passing the previous state in is what keeps this a pure function of the
 * gesture so far rather than a hidden latch inside the control.
 */
function axisSign(
  value: number,
  previous: -1 | 0 | 1,
  engage: number,
  flip: number,
  release: number,
): -1 | 0 | 1 {
  if (previous > 0) {
    if (value < -flip) return -1
    return value <= release ? 0 : 1
  }
  if (previous < 0) {
    if (value > flip) return 1
    return value >= -release ? 0 : -1
  }
  if (value >= engage) return 1
  if (value <= -engage) return -1
  return 0
}

/** Map a clamped stick offset onto the four directions. */
export function stickActions(
  v: StickVector,
  previous: StickActions = NEUTRAL_STICK,
): StickActions {
  const x = axisSign(
    v.x,
    previous.right ? 1 : previous.left ? -1 : 0,
    STICK_DEADZONE_X,
    STICK_FLIP_X,
    STICK_RELEASE_X,
  )
  const y = axisSign(
    v.y,
    previous.down ? 1 : previous.up ? -1 : 0,
    STICK_DEADZONE_Y,
    STICK_FLIP_Y,
    STICK_RELEASE_Y,
  )
  return { left: x < 0, right: x > 0, up: y < 0, down: y > 0 }
}

/**
 * Should the on-screen controls be offered?
 *
 * `?touch=1` / `?touch=0` force the answer, which is how the desktop browser
 * and the Playwright runs exercise them. Otherwise a coarse pointer is the
 * signal; a device that only reports touch after the fact is handled by
 * `TouchControls`, which enables itself on the first real touch.
 */
export function touchUiAvailable(win: Window = window): boolean {
  try {
    const forced = new URLSearchParams(win.location.search).get('touch')
    if (forced === '1') return true
    if (forced === '0') return false
    return win.matchMedia('(pointer: coarse)').matches
  } catch {
    // A host without a real window (tests, SSR) is not a touch device.
    return false
  }
}

export interface TouchControlsOptions {
  input: Input
  /** Element the layers are appended to, normally `#stage`. */
  root: HTMLElement
  /** Force the controls on, for tests and the `?touch=1` preview. */
  force?: boolean
}

/**
 * Which layers a screen needs.
 *
 * `edit` is the touch answer to the keyboard's `E`: without it a phone has no
 * way into the course editor at all, because every other entry point is a key.
 */
function layers(state: string): {
  stick: boolean
  actions: boolean
  menu: boolean
  edit: boolean
  bar: 'none' | 'pause' | 'gameover'
} {
  switch (state) {
    case 'play':
      return {
        stick: true,
        actions: true,
        menu: false,
        edit: true,
        bar: 'none',
      }
    case 'select':
      // The stick navigates the list; the jump and run buttons would only be
      // in the way of the tap that starts a course.
      return {
        stick: true,
        actions: false,
        menu: true,
        edit: true,
        bar: 'none',
      }
    case 'title':
      return {
        stick: false,
        actions: false,
        menu: true,
        edit: true,
        bar: 'none',
      }
    case 'won':
      return {
        stick: false,
        actions: false,
        menu: true,
        edit: false,
        bar: 'none',
      }
    case 'paused':
      return {
        stick: false,
        actions: false,
        menu: false,
        edit: true,
        bar: 'pause',
      }
    case 'gameover':
      return {
        stick: false,
        actions: false,
        menu: false,
        edit: false,
        bar: 'gameover',
      }
    default:
      return {
        stick: false,
        actions: false,
        menu: false,
        edit: false,
        bar: 'none',
      }
  }
}

/** Capture a pointer, tolerating hosts that refuse an inactive id. */
function capture(el: Element, pointerId: number): void {
  try {
    el.setPointerCapture?.(pointerId)
  } catch {
    // A synthetic or already-released pointer has nothing to capture; the
    // pointerup/pointercancel handlers still clear the action.
  }
}

export class TouchControls {
  private readonly input: Input
  private readonly layer: HTMLDivElement
  private readonly menu: HTMLDivElement
  private readonly stickZone: HTMLDivElement
  private readonly stickBase: HTMLDivElement
  private readonly stickKnob: HTMLDivElement
  private readonly jumpButton: HTMLButtonElement
  private readonly runButton: HTMLButtonElement
  private readonly pauseButton: HTMLButtonElement
  private readonly editButton: HTMLButtonElement
  private readonly actions: HTMLDivElement
  private readonly bar: HTMLDivElement
  private readonly barPrimary: HTMLButtonElement
  private readonly barSecondary: HTMLButtonElement

  private enabled: boolean
  private readonly forcedOff: boolean
  private state = 'title'
  private stickPointer: number | null = null
  private originX = 0
  private originY = 0
  /** Set while a pointer holds the stick, so moves never re-origin it. */
  private stickHeld = false
  /** Last frame's stick directions, for the engage/flip hysteresis. */
  private stickState: StickActions = { ...NEUTRAL_STICK }
  /**
   * Every pointer held down on the action pad, with the button under it now and
   * the button it landed on. The pair is what separates a *tap* (down and up on
   * the same button) from a *slide* (down on one, up on another).
   */
  private readonly padPointers = new Map<number, PadPointer>()

  constructor(options: TouchControlsOptions) {
    const { input, root } = options
    this.input = input
    this.forcedOff =
      new URLSearchParams(window.location.search).get('touch') === '0'
    this.enabled = options.force === true || touchUiAvailable()

    this.layer = document.createElement('div')
    this.layer.id = 'touch'
    this.layer.className = 'touch'

    this.menu = document.createElement('div')
    this.menu.className = 'touch-menu'
    this.menu.setAttribute('aria-hidden', 'true')

    this.stickZone = document.createElement('div')
    this.stickZone.className = 'touch-stick'
    this.stickZone.setAttribute('aria-hidden', 'true')
    this.stickBase = document.createElement('div')
    this.stickBase.className = 'touch-stick-base'
    this.stickKnob = document.createElement('div')
    this.stickKnob.className = 'touch-stick-knob'
    this.stickBase.append(this.stickKnob)
    this.stickZone.append(this.stickBase)

    this.runButton = this.button('touch-run', '»', 'Run and fire')
    this.jumpButton = this.button('touch-jump', '▲', 'Jump')
    this.pauseButton = this.button('touch-pause', '❚❚', 'Pause')
    this.editButton = this.button('touch-edit', '✎', 'Edit this course')
    const actions = document.createElement('div')
    actions.className = 'touch-actions'
    actions.append(this.runButton, this.jumpButton)

    this.barPrimary = this.button('touch-bar-primary', '', '')
    this.barSecondary = this.button('touch-bar-secondary', '', '')
    this.bar = document.createElement('div')
    this.bar.className = 'touch-bar'
    this.bar.append(this.barPrimary, this.barSecondary)

    const rotate = document.createElement('div')
    rotate.className = 'touch-rotate'
    rotate.textContent = '↻ Rotate your device for the full view'

    this.actions = actions

    this.layer.append(
      this.menu,
      this.stickZone,
      this.pauseButton,
      this.editButton,
      actions,
      this.bar,
      rotate,
    )
    root.append(this.layer)
    this.applyClass()
  }

  private button(
    className: string,
    label: string,
    aria: string,
  ): HTMLButtonElement {
    const b = document.createElement('button')
    b.type = 'button'
    b.className = `touch-btn ${className}`
    b.textContent = label
    if (aria) b.setAttribute('aria-label', aria)
    return b
  }

  /** True while the layers are live and should be drawn. */
  get active(): boolean {
    return this.enabled
  }

  /** The appended layer, exposed so a host can find or replace it. */
  get element(): HTMLElement {
    return this.layer
  }

  /**
   * Wire every pointer listener. Returns a teardown that also removes the layer.
   */
  attach(): () => void {
    const onStickDown = (e: PointerEvent): void => {
      if (!this.stickLive() || e.pointerType === 'mouse') return
      if (this.stickPointer !== null) return
      e.preventDefault()
      this.stickPointer = e.pointerId
      this.stickHeld = true
      this.originX = e.clientX
      this.originY = e.clientY
      this.stickBase.style.left = `${e.clientX}px`
      this.stickBase.style.top = `${e.clientY}px`
      this.setKnob(0, 0)
      this.stickBase.classList.add('active')
      capture(this.stickZone, e.pointerId)
    }
    const onStickMove = (e: PointerEvent): void => {
      if (!this.stickHeld || e.pointerId !== this.stickPointer) return
      e.preventDefault()
      const rawX = e.clientX - this.originX
      const rawY = e.clientY - this.originY
      const v = stickVector(rawX, rawY)
      this.setKnob(v.x, v.y)
      /**
       * Carry the origin along when the thumb travels past the rim.
       *
       * Without this the stick is a point the thumb has to come *back* to:
       * dragging far out and then reversing means sweeping the whole 62px
       * radius plus the dead zone, which is what "the dead zone feels much
       * bigger than 7px" really was. Recentring keeps the knob under the
       * thumb, so a reversal is always the short trip across the middle.
       */
      const excessX = rawX - v.x
      const excessY = rawY - v.y
      if (excessX !== 0 || excessY !== 0) {
        this.originX += excessX
        this.originY += excessY
        this.stickBase.style.left = `${this.originX}px`
        this.stickBase.style.top = `${this.originY}px`
      }
      this.applyStick(v)
    }
    const onStickUp = (e: PointerEvent): void => {
      if (e.pointerId !== this.stickPointer) return
      this.releaseStick()
    }

    /** Hold an action while a pointer is down; releases on cancel too. */
    const hold = (
      el: HTMLElement,
      action: InputAction | (() => InputAction),
    ): (() => void) => {
      const resolve = typeof action === 'function' ? action : () => action
      const down = (e: PointerEvent): void => {
        e.preventDefault()
        el.classList.add('pressed')
        this.input.setVirtual(resolve(), true)
        capture(el, e.pointerId)
      }
      const up = (): void => {
        el.classList.remove('pressed')
        this.input.setVirtual(resolve(), false)
      }
      el.addEventListener('pointerdown', down)
      el.addEventListener('pointerup', up)
      el.addEventListener('pointercancel', up)
      el.addEventListener('lostpointercapture', up)
      return () => {
        el.removeEventListener('pointerdown', down)
        el.removeEventListener('pointerup', up)
        el.removeEventListener('pointercancel', up)
        el.removeEventListener('lostpointercapture', up)
      }
    }

    const stops = [
      hold(this.menu, 'confirm'),
      // The pause button feeds the same action Escape does, so it pauses
      // instead of quitting: `cancel` is pause while playing.
      hold(this.pauseButton, 'cancel'),
      // `editor` is the same action the keyboard's E presses; the shell toggles
      // the editor on it in every screen.
      hold(this.editButton, 'editor'),
      hold(this.barPrimary, 'confirm'),
      // Which action the second bar button presses depends on the screen it was
      // drawn for, so it is resolved at press time rather than wired once.
      hold(this.barSecondary, () =>
        this.state === 'gameover' ? 'cancel' : 'quit',
      ),
    ]

    /**
     * The action pad: run latches, jump follows the finger.
     *
     * Running and jumping at the same time is the whole game, and a
     * hold-both-buttons layout needs a third thumb to do it. So run has two
     * ways in:
     *
     *   - **Tap it** and the latch sticks; the thumb is free to jump.
     *   - **Press and slide** off it (onto jump, usually) and run is held for as
     *     long as that finger is down, without touching the latch.
     *
     * The pointer's start button is what tells the two apart: a press that both
     * begins and ends on run is a tap, anything else is a slide. Jump follows
     * whichever button the finger is over, so a slide from run to jump presses
     * jump while run stays held.
     */
    const buttonUnder = (e: PointerEvent): 'jump' | 'run' | null => {
      const el = document.elementFromPoint(e.clientX, e.clientY)
      const btn = el?.closest?.('.touch-btn')
      if (btn === this.jumpButton) return 'jump'
      if (btn === this.runButton) return 'run'
      return null
    }
    const onPadDown = (e: PointerEvent): void => {
      if (e.pointerType === 'mouse') return
      e.preventDefault()
      const over = buttonUnder(e)
      this.padPointers.set(e.pointerId, {
        over,
        touched: new Set(over === null ? [] : [over]),
      })
      this.syncPad()
    }
    const onPadMove = (e: PointerEvent): void => {
      const state = this.padPointers.get(e.pointerId)
      if (state === undefined) return
      e.preventDefault()
      state.over = buttonUnder(e)
      if (state.over !== null) state.touched.add(state.over)
      this.syncPad()
    }
    const onPadUp = (e: PointerEvent): void => {
      if (!this.padPointers.delete(e.pointerId)) return
      this.syncPad()
    }
    this.actions.addEventListener('pointerdown', onPadDown)
    this.actions.addEventListener('pointermove', onPadMove)
    this.actions.addEventListener('pointerup', onPadUp)
    this.actions.addEventListener('pointercancel', onPadUp)

    const onContext = (e: Event): void => e.preventDefault()
    this.layer.addEventListener('contextmenu', onContext)

    /**
     * A real touch on a device that did not advertise a coarse pointer (an iPad
     * with a trackpad, a hybrid laptop) turns the controls on the moment it
     * happens. `?touch=0` opts out.
     */
    const onFirstTouch = (e: PointerEvent): void => {
      if (e.pointerType !== 'touch' || this.enabled || this.forcedOff) return
      this.enabled = true
      this.applyClass()
    }
    window.addEventListener('pointerdown', onFirstTouch, true)

    this.stickZone.addEventListener('pointerdown', onStickDown)
    this.stickZone.addEventListener('pointermove', onStickMove)
    this.stickZone.addEventListener('pointerup', onStickUp)
    this.stickZone.addEventListener('pointercancel', onStickUp)

    return () => {
      this.releaseStick()
      this.clearPad()
      for (const stop of stops) stop()
      this.actions.removeEventListener('pointerdown', onPadDown)
      this.actions.removeEventListener('pointermove', onPadMove)
      this.actions.removeEventListener('pointerup', onPadUp)
      this.actions.removeEventListener('pointercancel', onPadUp)
      this.layer.removeEventListener('contextmenu', onContext)
      window.removeEventListener('pointerdown', onFirstTouch, true)
      this.stickZone.removeEventListener('pointerdown', onStickDown)
      this.stickZone.removeEventListener('pointermove', onStickMove)
      this.stickZone.removeEventListener('pointerup', onStickUp)
      this.stickZone.removeEventListener('pointercancel', onStickUp)
      this.layer.remove()
    }
  }

  /** True while the stick layer should accept new touches. */
  private stickLive(): boolean {
    return this.enabled && (this.state === 'play' || this.state === 'select')
  }

  private setKnob(x: number, y: number): void {
    this.stickKnob.style.transform = `translate(${x}px, ${y}px)`
  }

  private applyStick(v: StickVector): void {
    const a = stickActions(v, this.stickState)
    this.stickState = a
    this.input.setVirtual('left', a.left)
    this.input.setVirtual('right', a.right)
    this.input.setVirtual('up', a.up)
    this.input.setVirtual('down', a.down)
  }

  private releaseStick(): void {
    this.stickHeld = false
    this.stickPointer = null
    this.stickState = { ...NEUTRAL_STICK }
    this.stickBase.classList.remove('active')
    this.setKnob(0, 0)
    this.input.setVirtual('left', false)
    this.input.setVirtual('right', false)
    this.input.setVirtual('up', false)
    this.input.setVirtual('down', false)
  }

  /** Push the pad's held state into the input layer and the visuals. */
  private syncPad(): void {
    const held = padHeld([...this.padPointers.values()])
    this.input.setVirtual('jump', held.jump)
    this.input.setVirtual('run', held.run)
    this.jumpButton.classList.toggle('pressed', held.jump)
    this.runButton.classList.toggle('pressed', held.holdingRun)
  }

  /** Drop the pad's held pointers. */
  private clearPad(): void {
    this.padPointers.clear()
    this.syncPad()
  }

  /**
   * Show the layers the current screen needs. Called every frame by the shell,
   * so it must stay cheap: the class string is only touched on a real change.
   */
  applyState(state: string): void {
    if (state === this.state) return
    this.state = state
    // Leaving a screen must not leave its buttons held: the next screen reads
    // none of them, but play does, and a stuck direction would run the player
    // into the first pit.
    this.releaseStick()
    this.clearPad()
    this.input.clearVirtual()
    this.applyClass()
  }

  private applyClass(): void {
    const l = layers(this.state)
    const cls = ['touch']
    if (this.enabled) cls.push('on')
    if (this.enabled && l.stick) cls.push('stick-on')
    if (this.enabled && l.actions) cls.push('actions-on')
    if (this.enabled && l.menu) cls.push('menu-on')
    if (this.enabled && l.bar !== 'none') cls.push(`bar-${l.bar}`)
    if (this.enabled && l.edit) cls.push('edit-on')
    this.layer.className = cls.join(' ')
    if (l.bar === 'pause') {
      this.barPrimary.textContent = 'RESUME'
      this.barPrimary.setAttribute('aria-label', 'Resume')
      this.barSecondary.textContent = 'QUIT TO COURSES'
      this.barSecondary.setAttribute('aria-label', 'Quit to course selection')
    } else if (l.bar === 'gameover') {
      this.barPrimary.textContent = 'RETRY'
      this.barPrimary.setAttribute('aria-label', 'Retry the course')
      this.barSecondary.textContent = 'COURSES'
      this.barSecondary.setAttribute('aria-label', 'Back to course selection')
    }
  }
}
